import {
  HttpContext,
  HttpContextToken,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';

import { inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MessageService } from '@openng/optimus-ui/api';
import { tap } from 'rxjs';
import {
  fileTooLargeMessage,
  invalidLessonFieldMessage,
  visibleApiErrorText,
  type ErrorTranslations,
} from '@app/shared/services/api-error';

const WITH_STATUS_MESSAGE = new HttpContextToken<{
  error: boolean;
  success: boolean;
  excludedErrorCodes?: number[];
}>(() => ({ error: true, success: false, excludedErrorCodes: [404] }));

export function withStatusMessage(
  error = true,
  success = true,
  excludedErrorCodes: number[] = [404]
) {
  return new HttpContext().set(WITH_STATUS_MESSAGE, {
    error,
    success,
    excludedErrorCodes,
  });
}

export const statusMessageInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);
  const translationService = inject(TranslocoService);

  return next(req).pipe(
    tap({
      next: res => {
        if (res instanceof HttpResponse) {
          if (req.context.get(WITH_STATUS_MESSAGE).success) {
            messageService.add({
              severity: 'success',
              summary: translationService.getTranslation('message')['success'],
            });
          }
        }
      },
      error: message => {
        const statusMessageConfig = req.context.get(WITH_STATUS_MESSAGE);
        const isExcludedError =
          statusMessageConfig.excludedErrorCodes?.includes(message?.status);

        if (statusMessageConfig.error && !isExcludedError) {
          const specificError =
            fileTooLargeMessage(
              message?.error?.errors,
              req.body,
              translationService
            ) ??
            invalidLessonFieldMessage(
              message?.error?.errors,
              translationService
            );
          if (specificError) {
            messageService.add({ severity: 'error', summary: specificError });
            return;
          }

          const errorsTranslations = translationService.getTranslation(
            'errors'
          ) as ErrorTranslations;
          const genericTranslation =
            errorsTranslations?.['An error has occured'];
          const generic =
            typeof genericTranslation === 'string' &&
            genericTranslation.trim() !== ''
              ? genericTranslation.trim()
              : translationService.translate('errors.An error has occured');
          messageService.add({
            severity: 'error',
            summary: visibleApiErrorText(
              message?.error,
              errorsTranslations,
              generic
            ),
          });
        }
      },
    })
  );
};
